import React, { FC, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { ActionSet } from 'models/action-set';
import { Action } from 'models/action';
import { useTranslation } from 'react-i18next';

const normalizeRegex = /[^a-z0-9]/gi;

type SearchActionOption = {
    label: string;
    id: string;
    actionId: string;
    number: string;
    subCategoryName: string;
    subCategoryId: string;
    categoryId: string;
    name: string;
    mainCategoryName: string;
    quotable?: boolean;
};

type SearchActionProps = {
    actionSet: ActionSet;
    onActionSelected?: (action: Action, categoryId: string, subCategoryId: string) => void;
    onActionCleared?: () => void;
    onTouched?: () => void;
};
const SearchAction: FC<SearchActionProps> = ({ actionSet, onActionSelected, onActionCleared, onTouched }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [selectedOption, setSelectedOption] = useState<SearchActionOption | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const showStyledValue = Boolean(selectedOption) && !isOpen;

    const uniqueActionOptions = (actions: Action[]) => {
        const uniqueActions: SearchActionOption[] = [];
        const uniqueMap = new Map<string, boolean>();
        if (actions?.length) {
            actions.forEach((action: Action) => {
                action.subCategory.map((subCat) => {
                    const uniqueKey = `${action._id}-${subCat._id}`;
                    if (uniqueMap.has(uniqueKey)) {
                        return;
                    }
                    uniqueMap.set(uniqueKey, true);
                    uniqueActions.push({
                        id: uniqueKey,
                        actionId: action._id || '',
                        label: `${action.number} - ${action.mainCategory.name} / ${subCat.name}: ${action.name}`,
                        number: action.number,
                        subCategoryName: subCat.name,
                        subCategoryId: subCat._id || '',
                        categoryId: action.mainCategory._id || '',
                        name: action.name,
                        mainCategoryName: action.mainCategory.name,
                        quotable: action?.quotable,
                    });
                });
            });
        }

        return uniqueActions.sort((a, b) => a.number.localeCompare(b.number));
    };

    const normalize = (str: string) => {
        return str.toLowerCase().replace(normalizeRegex, ' ').trim();
    };

    const tokenize = (str: string) => {
        return normalize(str).split(/\s+/).filter(Boolean);
    };

    const filterOptions = (options: SearchActionOption[], state: { inputValue: string }) => {
        const inputTokens = tokenize(state.inputValue);
        if (inputTokens.length === 0) return options;
        return options.filter((option) => {
            const labelTokens = tokenize(option.label);
            return inputTokens.every((token) => labelTokens.some((labelToken) => labelToken.includes(token)));
        });
    };

    const options = uniqueActionOptions(actionSet.actions);

    return (
        <Box>
            <Autocomplete
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                onFocus={onTouched}
                size="medium"
                fullWidth
                disablePortal
                options={options}
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Box display="flex">
                            <Box>
                                {option.number} {option.name}
                            </Box>
                            <Box sx={{ marginLeft: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                                {option.mainCategoryName} / {option.subCategoryName}{' '}
                                {option?.quotable ? (
                                    <Typography
                                        component="strong"
                                        sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: 'inherit' }}
                                    >
                                        {`(${translate('common:QUOTABLE')})`}
                                    </Typography>
                                ) : (
                                    ''
                                )}
                            </Box>
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={translate('orders:ALL_ACTIONS')}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: showStyledValue ? (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        pointerEvents: 'none',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.primary' }}>
                                        {selectedOption!.label}
                                    </Typography>
                                    {selectedOption!.quotable && (
                                        <Typography
                                            component="strong"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: 'warning.main',
                                                fontSize: 'inherit',
                                                ml: 0.5,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {`(${translate('common:QUOTABLE')})`}
                                        </Typography>
                                    )}
                                </Box>
                            ) : undefined,
                        }}
                        inputProps={{
                            ...params.inputProps,
                            style: {
                                ...(params.inputProps as React.InputHTMLAttributes<HTMLInputElement>).style,
                                opacity: showStyledValue ? 0 : 1,
                            },
                        }}
                    />
                )}
                filterOptions={filterOptions}
                autoHighlight
                onChange={(_, value) => {
                    setSelectedOption(value);
                    if (value) {
                        const action = actionSet.actions.find(
                            (a) =>
                                a._id === value.actionId &&
                                a.subCategory.some((sub) => sub._id === value.subCategoryId),
                        );
                        if (action && onActionSelected) {
                            onActionSelected(action, value.categoryId, value.subCategoryId);
                        }
                    } else if (onActionCleared) {
                        onActionCleared();
                    }
                }}
            />
        </Box>
    );
};

export default SearchAction;
